/**
 * /arena/plans — Arena Pass subscription page (Session 2).
 * Three plans: Basic / Plus / Pro. 14-day free trial, then Razorpay one-time
 * order for renewals.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Check } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { ArenaShield } from "../../components/arena/ArenaAtoms";

const PLANS = [
  { key: "basic", price: 999, label: "Basic", credits: 10, perks: ["10 visits / month", "Tier 3 gyms", "All FitFrik features"] },
  { key: "plus", price: 1799, label: "Plus", credits: 25, perks: ["25 visits / month", "Tier 2 + 3 gyms", "1 trainer intro session"], popular: true },
  { key: "pro", price: 2999, label: "Pro", credits: "∞", perks: ["Unlimited visits", "All tiers", "Priority check-in", "1 trainer session/month"] },
];

export default function ArenaPlans() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = () => apiClient.get("/arena/pass/me").then((r) => setSub(r.data?.subscription || null));
  useEffect(() => { load(); }, []);

  const startTrial = async (plan_type) => {
    setBusy(true);
    try {
      await apiClient.post("/arena/pass/start-trial", { plan_type });
      toast.success("14-day free trial started");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const startCharge = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/arena/pass/create-order");
      // Razorpay checkout
      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "FitFrik Arena Pass",
        description: `${data.plan_type.toUpperCase()} plan`,
        handler: async (resp) => {
          try {
            await apiClient.post("/arena/pass/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Pass activated!");
            load();
          } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
        },
        theme: { color: "#F5C518" },
      });
      rzp.open();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your Arena Pass? You'll keep access until the end of the billing period.")) return;
    try {
      await apiClient.post("/arena/pass/cancel");
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const redeemFp = async () => {
    try {
      const { data } = await apiClient.post("/arena/pass/redeem-fitpoints");
      toast.success(`+1 visit credit (${data.fp_balance} FP left, ${data.redeems_used_this_month}/${data.max_per_month} this month)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not redeem"); }
  };

  // Lazy-load Razorpay JS
  useEffect(() => {
    if (window.Razorpay) return;
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-plans">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto flex items-center px-4 py-3 gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" style={{ background: "transparent", border: "none" }}><ChevronLeft size={22} /></button>
          <ArenaShield />
          <span style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 800 }}>Arena Pass</span>
        </div>
      </header>
      <main className="max-w-md mx-auto px-5 pt-4 pb-32">
        <h1 style={{ fontFamily: "Barlow Condensed", fontSize: 30, fontWeight: 900, lineHeight: 1.1 }}>Train at 500+ gyms across India</h1>
        <p style={{ fontFamily: "Barlow", fontSize: 14, color: "var(--text-secondary, #4A4845)", marginTop: 6 }}>One pass. Hundreds of gyms. 14 days free.</p>

        {sub && (
          <div style={{ marginTop: 18, padding: 16, background: "linear-gradient(135deg, #F5C518 0%, #E4A30C 100%)", borderRadius: 16, color: "#0C0C0B" }} data-testid="active-pass">
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>{sub.status === "trial" ? "Trial" : "Active"}</div>
            <div style={{ fontFamily: "Barlow Condensed", fontSize: 30, fontWeight: 900, marginTop: 2 }}>{sub.plan_type.toUpperCase()}</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {sub.credits_total - sub.credits_used} of {sub.credits_total === 99999 ? "∞" : sub.credits_total} visits left
            </div>
            <div style={{ fontSize: 11, marginTop: 4 }}>Renews: {new Date(sub.next_billing_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              {sub.status === "trial" && (
                <button onClick={startCharge} disabled={busy} data-testid="charge-now"
                  style={{ flex: 1, height: 40, background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 13, fontWeight: 700 }}>
                  {busy ? "Loading…" : "Activate now"}
                </button>
              )}
              <button onClick={cancel} data-testid="cancel-pass"
                style={{ flex: 1, height: 40, background: "transparent", color: "#0C0C0B", border: "1px solid rgba(12,12,11,0.30)", borderRadius: 100, fontFamily: "Barlow", fontSize: 13, fontWeight: 600 }}>
                Cancel
              </button>
            </div>
            <button onClick={redeemFp} data-testid="redeem-fp"
              style={{ marginTop: 10, width: "100%", padding: "8px 12px", background: "rgba(12,12,11,0.10)", color: "#0C0C0B", border: "1px dashed rgba(12,12,11,0.30)", borderRadius: 100, fontFamily: "Barlow", fontSize: 12, fontWeight: 700 }}>
              Redeem 500 FitPoints for 1 visit
            </button>
          </div>
        )}

        {!sub && (
          <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
            {PLANS.map((p) => (
              <button key={p.key} disabled={busy} onClick={() => startTrial(p.key)} data-testid={`start-trial-${p.key}`}
                style={{
                  textAlign: "left", padding: 18, position: "relative",
                  background: p.popular ? "#0C0C0B" : "var(--bg-surface, #E4E2DE)",
                  color: p.popular ? "white" : "var(--text-primary, #0C0C0B)",
                  border: "1px solid", borderColor: p.popular ? "#F5C518" : "var(--border, rgba(12,12,11,0.10))",
                  borderRadius: 16,
                }}>
                {p.popular && <span style={{ position: "absolute", top: -10, right: 16, background: "#F5C518", color: "#0C0C0B", fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 100, letterSpacing: "0.08em" }}>MOST POPULAR</span>}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontFamily: "Barlow Condensed", fontSize: 24, fontWeight: 800 }}>{p.label}</span>
                  <span style={{ fontFamily: "Barlow Condensed", fontSize: 26, fontWeight: 900 }}>
                    ₹{p.price}<span style={{ fontSize: 12, fontWeight: 500, opacity: 0.7 }}>/mo</span>
                  </span>
                </div>
                <ul style={{ margin: "12px 0 0 0", padding: 0, listStyle: "none", fontSize: 13 }}>
                  {p.perks.map((pt, i) => <li key={i} style={{ display: "flex", gap: 8, padding: "3px 0" }}><Check size={14} style={{ marginTop: 3, color: "#F5C518", flexShrink: 0 }} /> {pt}</li>)}
                </ul>
                <div style={{ marginTop: 12, fontSize: 11, fontWeight: 700, color: "#F5C518" }}>14-day free trial · cancel anytime</div>
              </button>
            ))}
          </div>
        )}

        <p style={{ marginTop: 22, fontSize: 11, color: "var(--text-tertiary, #8A8884)", textAlign: "center" }}>
          Refund: Day 1-10 = 70% · Day 11-20 = 40% · Day 21+ = no refund. Trial cancel before day 14 = no charge.
        </p>
      </main>
    </div>
  );
}
